import { createModel } from "xstate/lib/model";
import {
  IInvoice,
  invoiceHistory,
  sendInvoiceOnMail,
} from "@nju/data-access-portal-payment";
import { BasicError, isOk } from "@nju/result";

interface IContext {
  invoices: IInvoice[];
  error?: BasicError;
  activeInvoiceId?: string;
}

type IEvent =
  | { type: "INVOICES_RECEIVED"; invoices: IInvoice[] }
  | { type: "INVOICES_FETCH_ERROR"; error: BasicError }
  | { type: "SEND"; id: string }
  | { type: "SEND_SUCCESS" }
  | { type: "SEND_ERROR" }
  | { type: "BACK" };

const model = createModel<IContext, IEvent>({
  invoices: [],
  activeInvoiceId: undefined,
});

const setInvoice = model.assign((_, { invoices }) => {
  return { invoices };
}, "INVOICES_RECEIVED");

const setError = model.assign((_, { error }) => {
  return { error };
}, "INVOICES_FETCH_ERROR");

const setActiveInvoice = model.assign((_, { id }) => {
  return { activeInvoiceId: id };
}, "SEND");

export const invoiceHistoryMachine = model.createMachine(
  {
    /**
     * Ten klucz będzie działał jako default w v5
     * @see https://github.com/statelyai/xstate/blob/main/packages/core/CHANGELOG.md#4230
     */
    preserveActionOrder: true,
    initial: "fetchInvoices",
    states: {
      fetchInvoices: {
        invoke: {
          src: "fetchInvoices",
        },
        on: {
          INVOICES_RECEIVED: {
            actions: setInvoice,
            target: "idle",
          },
          INVOICES_FETCH_ERROR: {
            actions: setError,
            target: "error",
          },
        },
      },
      idle: {
        always: [
          {
            cond: "noInvoices",
            target: "empty",
          },
        ],
        on: {
          SEND: {
            actions: setActiveInvoice,
            target: "send",
          },
        },
      },
      send: {
        tags: ["loading"],
        on: {
          SEND_SUCCESS: {
            target: "sent",
          },
          SEND_ERROR: {
            target: "error",
          },
        },
        invoke: {
          src: ({ activeInvoiceId }) => {
            return async (callback) => {
              if (!activeInvoiceId) {
                return callback({ type: "SEND_ERROR" });
              }
              const result = await sendInvoiceOnMail({
                invoiceId: activeInvoiceId,
              });
              if (isOk(result)) {
                callback({ type: "SEND_SUCCESS" });
              } else {
                callback({ type: "SEND_ERROR" });
              }
            };
          },
        },
      },
      sent: {
        on: {
          BACK: {
            target: "idle",
          },
        },
      },
      empty: {
        entry: "redirectToPayments",
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      noInvoices: ({ invoices }) => {
        return invoices.length === 0;
      },
    },
    services: {
      fetchInvoices: () => {
        return async (callback) => {
          try {
            const { invoice: invoices } = (
              await invoiceHistory({ state: "SENT" })
            ).unwrap();
            callback({ type: "INVOICES_RECEIVED", invoices });
          } catch (error) {
            if (error instanceof BasicError) {
              callback({ type: "INVOICES_FETCH_ERROR", error });
            }
          }
        };
      },
    },
  },
);
